import _Button from "@mui/material/Button";
import _FormControl from "@mui/material/FormControl";
import _FormControlLabel from "@mui/material/FormControlLabel";
import _Checkbox from "@mui/material/Checkbox";
import _Typography from "@mui/material/Typography";
import _InputAdornment from "@mui/material/InputAdornment";
import _IconButton from "@mui/material/IconButton";
import _VisibilityOff from "@mui/icons-material/VisibilityOff";
import _Visibility from "@mui/icons-material/Visibility";
import _TextField from "@mui/material/TextField";
import { useFormik } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { styled } from 'styled-components';
import * as Yup from 'yup';
import { AuthService } from '../../services';
const OTP_LENGTH = 6;
const OTP_RESEND_COOLDOWN_SECONDS = 60;
function SignInForm({
  className,
  onSignedIn
}) {
  const [view, setView] = useState('otp-request');
  const [passwordShowed, showPassword] = useState(false);
  const [otpDigits, setOtpDigits] = useState(Array(OTP_LENGTH).fill(''));
  const [otpTargetEmail, setOtpTargetEmail] = useState('');
  const [otpError, setOtpError] = useState('');
  const [isOtpVerifying, setIsOtpVerifying] = useState(false);
  const [resendCooldownSeconds, setResendCooldownSeconds] = useState(0);
  const otpInputDigitsRefs = useRef([]);
  const {
    t,
    i18n: {
      language
    }
  } = useTranslation();
  const {
    isSubmitting,
    values,
    touched,
    errors,
    setFieldValue,
    handleChange,
    handleSubmit
  } = useFormik({
    initialValues: {
      email: '',
      password: '',
      rememberMe: false
    },
    validationSchema: Yup.object().shape({
      email: Yup.string().email(t('commons.sign_in_form.email_invalid') || '').required(t('commons.sign_in_form.email_required') || ''),
      password: Yup.string().when([], {
        is: () => view === 'password',
        then: schema => schema.required(),
        otherwise: schema => schema.notRequired()
      })
    }),
    enableReinitialize: true,
    validateOnMount: true,
    onSubmit
  });
  useEffect(() => {
    if (resendCooldownSeconds <= 0) return;
    const timeoutId = window.setTimeout(() => {
      setResendCooldownSeconds(currentSeconds => Math.max(currentSeconds - 1, 0));
    }, 1000);
    return () => window.clearTimeout(timeoutId);
  }, [resendCooldownSeconds]);
  async function onSubmit({
    email,
    password,
    rememberMe
  }, {
    setSubmitting,
    setErrors
  }) {
    if (!email) return;
    setSubmitting(true);
    try {
      if (view === 'password') {
        if (!password) {
          setSubmitting(false);
          return;
        }
        const user = await AuthService.signInWithEmail(email, password, rememberMe, language);
        onSignedIn(user);
      } else {
        await AuthService.generateOtpEmailCode(email);
        setOtpTargetEmail(email);
        setOtpDigits(Array(OTP_LENGTH).fill(''));
        setOtpError('');
        setResendCooldownSeconds(OTP_RESEND_COOLDOWN_SECONDS);
        setView('otp-verification');
        setSubmitting(false);
      }
    } catch (err) {
      setErrors({
        email: t(typeof err === 'object' && err && 'code' in err && err.code === 429 ? 'commons.sign_in_form.too_many_attempts' : 'commons.sign_in_form.invalid_credentials') || ''
      });
      if (view === 'password') {
        setFieldValue('password', '', false);
      }
      setSubmitting(false);
    }
  }
  async function onVerifyOtp() {
    const otp = otpDigits.join('');
    if (otp.length !== OTP_LENGTH || !otpTargetEmail) {
      setOtpError(t('commons.sign_in_form.invalid_credentials') || '');
      return;
    }
    setIsOtpVerifying(true);
    setOtpError('');
    try {
      const user = await AuthService.signInWithOtpEmail(otpTargetEmail, otp, values.rememberMe);
      onSignedIn(user);
    } catch (err) {
      setOtpError(t(typeof err === 'object' && err && 'code' in err && err.code === 429 ? 'commons.sign_in_form.too_many_attempts' : 'commons.sign_in_form.invalid_credentials') || '');
      setIsOtpVerifying(false);
    }
  }
  async function onResendOtp() {
    if (!otpTargetEmail) return;
    setOtpError('');
    try {
      await AuthService.generateOtpEmailCode(otpTargetEmail);
      setResendCooldownSeconds(OTP_RESEND_COOLDOWN_SECONDS);
    } catch (err) {
      setOtpError(t(typeof err === 'object' && err && 'code' in err && err.code === 429 ? 'commons.sign_in_form.too_many_attempts' : 'commons.sign_in_dialog.server_error') || '');
    }
  }
  function fillOtpDigitsFromCode(rawValue) {
    const sanitizedValue = rawValue.replace(/\D/g, '');
    if (sanitizedValue.length !== OTP_LENGTH) return false;
    setOtpDigits(sanitizedValue.split(''));
    otpInputDigitsRefs.current[OTP_LENGTH - 1]?.focus();
    return true;
  }
  function updateOtpDigit(index, rawValue) {
    const sanitizedValue = rawValue.replace(/\D/g, '');
    if (!sanitizedValue && rawValue) return;
    if (sanitizedValue.length > 1 && fillOtpDigitsFromCode(sanitizedValue)) return;
    const newDigits = [...otpDigits];
    newDigits[index] = sanitizedValue.slice(-1);
    setOtpDigits(newDigits);
    if (sanitizedValue && index < otpInputDigitsRefs.current.length - 1) otpInputDigitsRefs.current[index + 1]?.focus();
  }
  function onOtpKeyDown(event, index) {
    if (event.key === 'Backspace' && !otpDigits[index] && index > 0) otpInputDigitsRefs.current[index - 1]?.focus();
  }
  function onOtpPaste(event) {
    const pastedText = event.clipboardData.getData('text');
    if (!fillOtpDigitsFromCode(pastedText)) return;
    event.preventDefault();
  }
  function showPasswordView() {
    setView('password');
    setOtpError('');
  }
  return /*#__PURE__*/React.createElement(StyledForm, {
    className: className,
    onSubmit: handleSubmit
  }, /*#__PURE__*/React.createElement(_TextField, {
    disabled: isSubmitting || isOtpVerifying || view === 'otp-verification',
    error: Boolean(errors.email && touched.email),
    helperText: touched.email && errors.email,
    id: "email",
    label: /*#__PURE__*/React.createElement(Trans, {
      i18nKey: "commons.sign_in_form.email"
    }),
    margin: "dense",
    name: "email",
    onChange: handleChange,
    size: "small",
    type: "email",
    value: values.email,
    variant: "outlined"
  }), view === 'password' && /*#__PURE__*/React.createElement(_TextField, {
    disabled: isSubmitting,
    id: "password",
    label: /*#__PURE__*/React.createElement(Trans, {
      i18nKey: "commons.sign_in_form.password"
    }),
    margin: "dense",
    name: "password",
    onChange: handleChange,
    size: "small",
    slotProps: {
      input: {
        endAdornment: /*#__PURE__*/React.createElement(_InputAdornment, {
          position: "end"
        }, /*#__PURE__*/React.createElement(_IconButton, {
          "aria-label": t('commons.sign_in_form.toggle_password_visibility'),
          onClick: () => showPassword(!passwordShowed),
          size: "small"
        }, passwordShowed ? /*#__PURE__*/React.createElement(_Visibility, null) : /*#__PURE__*/React.createElement(_VisibilityOff, null)))
      }
    },
    type: passwordShowed ? 'text' : 'password',
    value: values.password,
    variant: "outlined"
  }), view === 'otp-verification' && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(_Typography, {
    color: "textSecondary",
    variant: "body2"
  }, /*#__PURE__*/React.createElement(Trans, {
    i18nKey: "commons.sign_in_form.otp_verification_title"
  })), /*#__PURE__*/React.createElement(_Typography, {
    color: "textSecondary",
    variant: "caption"
  }, /*#__PURE__*/React.createElement(Trans, {
    i18nKey: "commons.sign_in_form.otp_verification_description",
    values: {
      email: otpTargetEmail
    }
  })), /*#__PURE__*/React.createElement(_Typography, {
    color: "textSecondary",
    variant: "caption"
  }, /*#__PURE__*/React.createElement(Trans, {
    i18nKey: "commons.sign_in_form.otp_code_validity",
    values: {
      minutes: 5
    }
  })), /*#__PURE__*/React.createElement(OtpDigitsWrapper, null, otpDigits.map((digit, index) => /*#__PURE__*/React.createElement(_TextField, {
    autoComplete: "one-time-code",
    disabled: isOtpVerifying,
    inputRef: input => {
      otpInputDigitsRefs.current[index] = input;
    },
    key: `otp-${index}`,
    margin: "dense",
    onChange: event => updateOtpDigit(index, event.target.value),
    onKeyDown: event => onOtpKeyDown(event, index),
    onPaste: onOtpPaste,
    slotProps: {
      htmlInput: {
        inputMode: 'numeric',
        maxLength: 1,
        pattern: '[0-9]*'
      }
    },
    sx: {
      flex: 1
    },
    value: digit
  }))), otpError && /*#__PURE__*/React.createElement(_Typography, {
    color: "error",
    variant: "caption"
  }, otpError), /*#__PURE__*/React.createElement(StyledButton, {
    color: "primary",
    disabled: isOtpVerifying || otpDigits.some(digit => !digit),
    onClick: onVerifyOtp,
    size: "medium",
    type: "button",
    variant: "contained"
  }, /*#__PURE__*/React.createElement(Trans, {
    i18nKey: "commons.sign_in_form.verify_otp_code"
  })), /*#__PURE__*/React.createElement(StyledTextButton, {
    disabled: isOtpVerifying || resendCooldownSeconds > 0,
    onClick: onResendOtp,
    type: "button"
  }, resendCooldownSeconds > 0 ? t('commons.sign_in_form.resend_otp_code_with_timer', {
    seconds: resendCooldownSeconds
  }) : t('commons.sign_in_form.resend_otp_code'))), /*#__PURE__*/React.createElement(_FormControl, {
    component: "fieldset",
    disabled: isSubmitting,
    margin: "dense"
  }, /*#__PURE__*/React.createElement(_FormControlLabel, {
    control: /*#__PURE__*/React.createElement(_Checkbox, {
      checked: values.rememberMe,
      color: "primary",
      name: "rememberMe",
      onChange: handleChange
    }),
    label: /*#__PURE__*/React.createElement(_Typography, {
      variant: "caption"
    }, /*#__PURE__*/React.createElement(Trans, {
      i18nKey: "commons.sign_in_form.remember_me"
    }))
  })), view !== 'otp-verification' && /*#__PURE__*/React.createElement(StyledButton, {
    color: "primary",
    disabled: isSubmitting,
    size: "medium",
    type: "submit",
    variant: "contained"
  }, view === 'password' ? /*#__PURE__*/React.createElement(Trans, {
    i18nKey: "commons.actions.sign_in"
  }) : /*#__PURE__*/React.createElement(Trans, {
    i18nKey: "commons.sign_in_form.request_otp_code"
  })), view === 'otp-request' && /*#__PURE__*/React.createElement(StyledTextButton, {
    onClick: showPasswordView,
    type: "button"
  }, /*#__PURE__*/React.createElement(Trans, {
    i18nKey: "commons.sign_in_form.sign_in_with_password"
  })), view === 'password' && /*#__PURE__*/React.createElement(StyledTextButton, {
    onClick: () => {
      setView('otp-request');
      setFieldValue('password', '', false);
    },
    type: "button"
  }, /*#__PURE__*/React.createElement(Trans, {
    i18nKey: "commons.sign_in_form.sign_in_with_otp"
  })));
}
const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
`;
const StyledButton = styled(_Button)`
  && {
    margin-top: 8px;
  }
`;
const StyledTextButton = styled(_Button)`
  && {
    margin-top: 8px;
    text-transform: none;
  }
`;
const OtpDigitsWrapper = styled.div`
  display: flex;
  gap: 8px;
  width: 100%;
`;
export default SignInForm;